import {Dictionary, convertAllErrorsFromBeToFormError, putAll, takeLatestF, validateSchema} from '@fl/cmsch-fe-library';
import {isUndefined, keys, omitBy} from 'lodash/fp';
import {SagaIterator} from 'redux-saga';
import {isEmpty, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {ValidateCreateOtherSubjectBindingAdmin} from 'api/gen/ValidateCreateOtherSubjectBindingAdmin';
import {ValidateOtherSubjectBindingAdmin} from 'api/gen/ValidateOtherSubjectBindingAdmin';
import {t} from 'app/translations';
import {canRunAsyncValidation, formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {ValidateOtherSubjectErrorsSchema} from '../../types/errors/validate-other-subject-errors';
import {SubjectFormValuesErrors, subjectFormName} from '../../types/subject-form-values';
import {ValidateOtherSubjectsAction} from '../action';

interface ApiTypes {
    associationId: number;
    roleIds: Array<number> | null;
    userProfileId: number;
    approvedFrom: string;
    approvedTo: string | null;
    userProfileAssociationRoleIds: Array<number> | null;
}
const getApiValues = ({
    associationId,
    roleIds,
    userProfileId,
    approvedFrom,
    approvedTo,
    userProfileAssociationRoleIds,
}: ApiTypes): ValidateOtherSubjectBindingAdmin => isEmpty(userProfileAssociationRoleIds)
    ? ({
        tag: 'ValidateCreateOtherSubjectBindingAdmin',
        binding: {
            roleIds: opt(roleIds).orCrash('Users associations form: roleIds not found'),
            otherSubjectId: associationId,
            userProfileId,
            approvedFrom,
            approvedTo,
        },
    })
    : ({
        tag: 'ValidateUpdateOtherSubjectBindingAdmin',
        binding: {
            userProfileOtherSubjectRoleIds: opt(userProfileAssociationRoleIds)
                .orCrash('Users associations form: userProfileAssociationRoleIds not found'),
            approvedTo,
        },
    });

const mapErrors = (
    errors: Dictionary<keyof ValidateCreateOtherSubjectBindingAdmin, string>,
): SubjectFormValuesErrors => {
    const validErrors = validateSchema(ValidateOtherSubjectErrorsSchema, 'FE')(errors).binding;

    return omitBy(isUndefined)<SubjectFormValuesErrors>({
        approvedFrom: validErrors.approvedFrom,
        approvedTo: validErrors.approvedTo,
        subjectId: validErrors.otherSubjectId,
        roleIds: validErrors.roleIds,
    });
};

// eslint-disable-next-line max-lines-per-function
function* execute({payload: {source}}: ValidateOtherSubjectsAction): SagaIterator {
    const {
        approvedFrom,
        approvedTo,
        subjectId,
        roleIds,
        userProfileAssociationRoleIds,
        userProfileId,
    } = (
        yield* select(formHelpers.formValues(subjectFormName))
    ).orCrash(`no ${subjectFormName} form values`);

    const pristine = yield* select(formHelpers.isPristine(subjectFormName));

    const syncErrors = yield* select(formHelpers.getSyncErrors(subjectFormName));
    if (syncErrors.exists(errors => !canRunAsyncValidation(errors, source))) return;

    if (
        approvedFrom === null
        || subjectId === null
        || userProfileId === null
        || isEmpty(roleIds)
        || pristine
        || (approvedTo !== null && approvedFrom > approvedTo)
    ) return; // synchronously not valid

    yield* put(formHelpers.startAsyncValidation(subjectFormName));

    const response = yield* call(
        Api.validateUserProfileOtherSubjectRoleBinding,
        getApiValues({
            associationId: subjectId,
            roleIds,
            userProfileId,
            approvedFrom,
            approvedTo,
            userProfileAssociationRoleIds,
        }),
    );

    if (!response.isSuccess) {
        if (response.isBadRequest) {
            const errors = convertAllErrorsFromBeToFormError(response.data);
            const otherSubjectErrors = mapErrors(errors.errors);
            yield* put(formHelpers.stopAsyncValidation(subjectFormName, otherSubjectErrors));
            const fieldsWithNewErrors = keys(otherSubjectErrors) as Array<keyof typeof otherSubjectErrors>;

            if (!isEmpty(fieldsWithNewErrors)) {
                yield* put(formHelpers.touch(subjectFormName, ...fieldsWithNewErrors));
            }
        } else {
            yield putAll(showBeError(response, t('common')('validateEntry')));
        }
    } else {
        yield* put(formHelpers.stopAsyncValidation(subjectFormName, {}));
    }
}

export function* validateOtherSubjectsSaga(): SagaIterator {
    yield takeLatestF('userDetail/VALIDATE_OTHER_SUBJECTS', execute);
}
